import * as React from 'react';

import './index.scss';

import Toolbar from './Toolbar';
import PropertyBar from './PropertyBar';
import PathEditBar from './PathEditBar/index';
import FontLoadingIndicator from './FontLoadingIndicator';
import BottomToolbar from './BottomToolbar';
import ShortcutCheatsheet from './ShortcutCheatsheet';
import { App } from '../../../pkg';
import { registerResize } from '../../utils/dom-events';
import { makeLooper } from '../../utils/loop';
import Fps from './Fps';
import Textarea from './Textarea';
import { CursorDrawMode } from '../../models/cursor';
import { registerEventReceiver, onTextEdit } from '../../wasmEventReceiver';
import { selectImage } from '../../utils/file';
import { imageStorage } from '../../service/image_storage';
import { getCanvasSizeInfo } from '../../utils/dom-utils';
import { mapKey } from '../../utils/key';
import { FontManager } from '../../utils/fontManager';
import { editorStorage } from '../../service/editor_storage';

interface IApplicationProps {
  app: App;
  fontManager: FontManager;
}

interface IPathEditingAppApi {
  is_path_editing?: () => boolean;
  is_path_closed?: () => boolean;
  close_editing_path?: () => void;
  finish_path_editing?: () => void;
  get_selection_count?: () => number;
}

interface IPersistAppApi {
  export_editor_json?: () => string;
  export_camera_json?: () => string;
}

enum MouseEventType {
  Down,
  Up,
  Move,
  DblClick,
}

const getCanvasPositionFromClient = (
  clientX: number,
  clientY: number,
): { x: number; y: number; rect: DOMRect } | null => {
  const canvas = window.document.querySelector('canvas.app');
  if (!(canvas instanceof HTMLCanvasElement)) {
    return null;
  }
  const rect = canvas.getBoundingClientRect();
  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
    rect,
  };
};

const toScreenPositionFromCanvas = (x: number, y: number): { left: number; top: number } => {
  const canvas = window.document.querySelector('canvas.app');
  if (!(canvas instanceof HTMLCanvasElement)) {
    return { left: x, top: y };
  }
  const rect = canvas.getBoundingClientRect();
  return {
    left: x + rect.left,
    top: y + rect.top,
  };
};

const resetAppSize = (app: App): void => {
  const sizeInfo = getCanvasSizeInfo(0);
  app.handle_resize(sizeInfo.width, sizeInfo.height, sizeInfo.dpr);
};

const formatShortcutKey = (key: string): string => {
  if (key === ' ') {
    return 'Space';
  }
  if (key.length === 1) {
    return key.toUpperCase();
  }
  if (key === 'Escape') {
    return 'Esc';
  }
  return key;
};

const formatShortcutFromEvent = (e: KeyboardEvent): string | null => {
  if (e.key === 'Alt' || e.key === 'Shift' || e.key === 'Control' || e.key === 'Meta') {
    return null;
  }
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) {
    parts.push('Cmd/Ctrl');
  }
  if (e.shiftKey) {
    parts.push('Shift');
  }
  if (e.altKey) {
    parts.push('Alt');
  }
  parts.push(formatShortcutKey(e.key));
  return parts.join(' + ');
};

const Application: React.FC<IApplicationProps> = ({ app, fontManager }: IApplicationProps) => {
  const looper = makeLooper(app);
  const [currentMode, setCurrentMode] = React.useState<CursorDrawMode>(CursorDrawMode.Cursor);
  const [version, setVersion] = React.useState(0);
  const pathEditingApi = app as unknown as IPathEditingAppApi;
  const [editText, setEditText] = React.useState<null | {
    left: number;
    top: number;
    viewLeft: number;
    viewTop: number;
    text: string;
    elementId?: string; // 可选的元素 ID，用于区分创建新文字还是编辑已有文字
    fontSize?: number;
    color?: string;
    fontFamily?: string;
  }>(null);
  const isPathEditing = pathEditingApi.is_path_editing?.() ?? false;
  const isPathClosed = pathEditingApi.is_path_closed?.() ?? false;
  const selectionCount = pathEditingApi.get_selection_count?.() ?? 0;
  const hasPropertyBar = !isPathEditing && selectionCount > 0;
  const autosaveTimerRef = React.useRef<number | null>(null);
  const autosaveInFlightRef = React.useRef(false);
  const autosaveRerunRef = React.useRef(false);
  const lastCanvasPointerRef = React.useRef<{ x: number; y: number } | null>(null);
  const shortcutHintTimerRef = React.useRef<number | null>(null);
  const [lastTriggeredShortcut, setLastTriggeredShortcut] = React.useState<string | null>(null);
  const [isShortcutCheatsheetOpen, setIsShortcutCheatsheetOpen] = React.useState(false);

  const openImagePicker = React.useCallback(async () => {
    const imageFile = await selectImage();
    const contentId = await imageStorage.saveImage(imageFile);
    const record = await imageStorage.getImage(contentId);
    if (record) {
      app.create_image(contentId, record.width, record.height);
      setVersion(v => v + 1);
    }
  }, [app]);

  const flushAutosave = React.useCallback(async () => {
    if (autosaveInFlightRef.current) {
      autosaveRerunRef.current = true;
      return;
    }

    autosaveInFlightRef.current = true;
    try {
      const persistAppApi = app as unknown as IPersistAppApi;
      const editorJson = persistAppApi.export_editor_json?.();
      const cameraJson = persistAppApi.export_camera_json?.();
      if (editorJson && cameraJson !== undefined) {
        await editorStorage.persistFromEditorJson(editorJson, cameraJson);
      }
    } catch (error) {
      console.error('Editor autosave failed:', error);
    } finally {
      autosaveInFlightRef.current = false;
      if (autosaveRerunRef.current) {
        autosaveRerunRef.current = false;
        void flushAutosave();
      }
    }
  }, [app]);

  const scheduleAutosave = React.useCallback(() => {
    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      void flushAutosave();
    }, 120);
  }, [flushAutosave]);

  React.useEffect(() => {
    looper.start();
    registerEventReceiver(evt => {
      if (evt === 'open_image_picker') {
        void openImagePicker();
      }
      setTimeout(() => {
        const mode = app.get_cursor_draw_mode();
        setCurrentMode(mode);
      }, 0);
      if (evt === 'editor_changed') {
        scheduleAutosave();
      }
    });

    // 监听文字编辑事件
    const unsubscribeTextEdit = onTextEdit((elementId: string) => {
      console.log('Edit text element:', elementId);
      const textInfo = app.get_text_element_info(elementId);
      console.log('textInfo from Rust:', textInfo);
      
      if (textInfo) {
        // Handle both Map (wasm-bindgen default for some structs) and Object
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const getValue = (key: string) => (textInfo instanceof Map ? textInfo.get(key) : (textInfo as any)[key]);

        const x = Number(getValue('x'));
        const y = Number(getValue('y'));
        
        if (!isNaN(x) && !isNaN(y)) {
          const id = getValue('id');
          const screenPosition = toScreenPositionFromCanvas(x, y);
          app.set_editing_id(id); // 通知 Rust 隐藏该元素
          setEditText({
            left: screenPosition.left,
            top: screenPosition.top,
            viewLeft: x,
            viewTop: y,
            text: getValue('text'),
            elementId: id,
            fontSize: getValue('fontSize'),
            color: getValue('fillColor'),
            fontFamily: getValue('fontFamily'),
          });
        } else {
          console.error('Invalid coordinates in textInfo:', textInfo);
        }
      }
    });

    return () => {
      if (autosaveTimerRef.current !== null) {
        window.clearTimeout(autosaveTimerRef.current);
      }
      if (shortcutHintTimerRef.current !== null) {
        window.clearTimeout(shortcutHintTimerRef.current);
      }
      unsubscribeTextEdit();
    };
  }, [app, fontManager, openImagePicker, scheduleAutosave]);

  React.useEffect(() => {
    resetAppSize(app);
    const resizeDebounceMs = 60;
    let resizeTimer: number | null = null;

    const handleResize = () => {
      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer);
      }
      resizeTimer = window.setTimeout(() => {
        resizeTimer = null;
        resetAppSize(app);
      }, resizeDebounceMs);
    };
    const customResizeEventName = registerResize();
    window.addEventListener(customResizeEventName, handleResize);

    return () => {
      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer);
      }
      window.removeEventListener(customResizeEventName, handleResize);
    };
  }, [app]);

  React.useEffect(() => {
    const finishTextEditing = () => {
      if (editText && editText.text.trim()) {
        if (editText.elementId) {
          // 编辑已有文字元素
          app.update_text_node(editText.elementId, editText.text.trim());
        } else {
          // 创建新文字元素
          app.create_text_node(
            editText.text.trim(),
            editText.viewLeft,
            editText.viewTop
          );
        }
      }
      app.set_editing_id(undefined); // 清空编辑 ID，恢复显示
      setEditText(null);
      app.change_cursor_draw_mode(CursorDrawMode.Cursor);
    };

    const handleMouseEvent = (eventType: MouseEventType, updateVersion: boolean, event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest('.canvas-overlay')) {
        return;
      }
      if (event.target instanceof Element && event.target.tagName === 'TEXTAREA') {
        return;
      }
      const canvasPosition = getCanvasPositionFromClient(event.clientX, event.clientY);
      if (!canvasPosition) {
        return;
      }
      const x = canvasPosition.x;
      const y = canvasPosition.y;
      lastCanvasPointerRef.current = { x, y };
      if (currentMode === CursorDrawMode.Text) {
        if (eventType === MouseEventType.Down) {
          if (!editText) {
            setEditText({
              left: event.clientX,
              top: event.clientY,
              viewLeft: x,
              viewTop: y,
              text: '',
            });
          } else {
            finishTextEditing();
          }
        }
        return;
      }
      app.handle_mouse(eventType, x, y, {
        shift: event.shiftKey,
        alt: event.altKey,
        ctrl: event.metaKey || event.ctrlKey,
      });
      if (updateVersion) {
        setVersion(Math.random());
      }
    };

    const syncModifierStateToMouse = (e: KeyboardEvent) => {
      const lastPointer = lastCanvasPointerRef.current;
      if (!lastPointer) {
        return;
      }
      app.handle_mouse(MouseEventType.Move, lastPointer.x, lastPointer.y, {
        shift: e.shiftKey,
        alt: e.altKey,
        ctrl: e.metaKey || e.ctrlKey,
      });
    };

    const handleMouseDown = handleMouseEvent.bind(null, MouseEventType.Down, false);
    document.addEventListener('mousedown', handleMouseDown);

    const handleMouseMove = handleMouseEvent.bind(null, MouseEventType.Move, false);
    document.addEventListener('mousemove', handleMouseMove);

    const handleMouseUp = handleMouseEvent.bind(null, MouseEventType.Up, true);
    document.addEventListener('mouseup', handleMouseUp);

    const handleDblClick = handleMouseEvent.bind(null, MouseEventType.DblClick, true);
    document.addEventListener('dblclick', handleDblClick);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isShortcutCheatsheetOpen) {
        if (e.key === 'Escape') {
          setIsShortcutCheatsheetOpen(false);
        }
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      // eslint-disable-next-line
      // @ts-ignore
      const tagName = e.target?.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
        return;
      }
      if (!e.metaKey && !e.ctrlKey && !e.altKey && e.key === '?') {
        setIsShortcutCheatsheetOpen(true);
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      if (currentMode === CursorDrawMode.Text) {
        if (e.key === 'Escape') {
          finishTextEditing();
        }
        return;
      }
      const selectionCountBefore = pathEditingApi.get_selection_count?.() ?? 0;
      const event_handled = app.handle_key_down({
        key:  mapKey(e.key),
        shift: e.shiftKey,
        alt: e.altKey,
        ctrl: e.metaKey || e.ctrlKey,
      });
      const selectionCountAfter = pathEditingApi.get_selection_count?.() ?? 0;
      if (selectionCountAfter !== selectionCountBefore) {
        setVersion(v => v + 1);
      }
      if (event_handled) {
        const activeElement = document.activeElement;
        if (
          activeElement instanceof HTMLButtonElement &&
          activeElement.classList.contains('tool-bar-button')
        ) {
          activeElement.blur();
        }
        const shortcutText = formatShortcutFromEvent(e);
        if (shortcutText) {
          setLastTriggeredShortcut(shortcutText);
          if (shortcutHintTimerRef.current !== null) {
            window.clearTimeout(shortcutHintTimerRef.current);
          }
          shortcutHintTimerRef.current = window.setTimeout(() => {
            setLastTriggeredShortcut(null);
            shortcutHintTimerRef.current = null;
          }, 1200);
        }
        e.stopPropagation();
        e.preventDefault();
      }
      if (e.key === 'Alt' || e.key === 'Shift' || e.key === 'Control' || e.key === 'Meta') {
        syncModifierStateToMouse(e);
      }
      console.log(event_handled);
      // let captured = true;
      // if (e.metaKey) {
        // switch (e.key) {
          // case 'a':
            // app.select_all();
            // break;
          // case 'g':
            // app.group_selection();
            // break;
          // case 's':
            // app.save();
            // break;
          // case '0':
          // case '1':
          // case '2':
          // case '3':
          // case '4':
          // case '5':
            // app.set_viewport(e.key);
            // break;
          // case 'z':
            // if (e.shiftKey) {
              // app.handle_redo();
            // } else {
              // app.handle_undo();
            // }
            // break;
          // case 'c':
            // app.handle_copy();
            // break;
          // case 'x':
            // app.handle_cut();
            // break;
          // case 'v':
            // app.handle_paste();
            // break;
          // case 'd':
            // app.handle_clone();
            // break;
          // default:
            // captured = false;
            // break;
        // }
      // } else {
        // switch (e.key) {
          // case 'Backspace':
            // app.handle_delete();
            // break;
          // case 'r':
            // app.change_cursor_draw_mode(CursorDrawMode.Rectangle);
            // break;
          // case 'o':
            // app.change_cursor_draw_mode(CursorDrawMode.Oval);
            // break;
          // case 't':
            // app.change_cursor_draw_mode(CursorDrawMode.Text);
            // break;
          // case 'Escape':
            // app.handle_escape_key();
            // break;
          // default:
            // captured = false;
            // break;
        // }
      // }
    };
    document.addEventListener('keydown', handleKeyDown);

    const handleKeyUp = (e: KeyboardEvent) => {
      if (isShortcutCheatsheetOpen) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      // eslint-disable-next-line
      // @ts-ignore
      const tagName = e.target?.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
        return;
      }
      if (currentMode === CursorDrawMode.Text) {
        if (e.key === 'Escape') {
          finishTextEditing();
        }
        return;
      }
      app.handle_key_up({
        key:  mapKey(e.key),
        shift: e.shiftKey,
        alt: e.altKey,
        ctrl: e.metaKey || e.ctrlKey,
      });
      if (e.key === 'Alt' || e.key === 'Shift' || e.key === 'Control' || e.key === 'Meta') {
        syncModifierStateToMouse(e);
      }
    };
    document.addEventListener('keyup', handleKeyUp);

    const handleWheel = (event: WheelEvent) => {
      if (currentMode === CursorDrawMode.Text) {
        return;
      }
      if (event.target instanceof Element && event.target.closest('.canvas-overlay')) {
        return;
      }
      // 阻止浏览器默认行为（包括 Mac 上的后退手势和页面缩放）
      event.preventDefault();

      // Mac 触控板双指缩放通常会触发带有 ctrlKey 的 wheel 事件
      if (event.ctrlKey || event.metaKey) {
        const canvasPosition = getCanvasPositionFromClient(event.clientX, event.clientY);
        if (!canvasPosition) {
          return;
        }
        // deltaY 在缩放手势中通常较小，除以一个系数来控制灵敏度
        // 注意：方向可能需要反转，尝试 -event.deltaY
        app.handle_zoom(-event.deltaY / 100, canvasPosition.x, canvasPosition.y);
      } else {
        app.handle_wheel(-event.deltaX, -event.deltaY);
      }
    };
    document.addEventListener('wheel', handleWheel, {
      passive: false,
    });

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('dblclick', handleDblClick);

      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);

      document.removeEventListener('wheel', handleWheel);
    };
  }, [currentMode, editText, app, isShortcutCheatsheetOpen]);

  return (
    <div className="application">
      <div className="left canvas-overlay">
        <Toolbar
          app={app}
          currentMode={currentMode}
          onOpenShortcutCheatsheet={() => {
            setIsShortcutCheatsheetOpen(true);
          }}
          onChangeMode={mode => {
            if (mode === CursorDrawMode.Image) {
              void openImagePicker();
            } else {
              app.change_cursor_draw_mode(mode);
            }
          }}
        />
      </div>
      {isPathEditing ? (
        <div className="top-overlay canvas-overlay">
          <PathEditBar
            isPathClosed={isPathClosed}
            onClosePath={() => {
              pathEditingApi.close_editing_path?.();
              setVersion(v => v + 1);
            }}
            onFinishEditing={() => {
              pathEditingApi.finish_path_editing?.();
              setVersion(v => v + 1);
            }}
          />
        </div>
      ) : null}
      {hasPropertyBar ? (
        <div className="top-overlay canvas-overlay">
          <PropertyBar app={app} version={version} onStyleChange={() => setVersion(v => v + 1)} />
        </div>
      ) : null}
      <Fps fps={looper.fps} />
      <FontLoadingIndicator fontManager={fontManager} />
      {lastTriggeredShortcut ? (
        <div className="shortcut-trigger-hint">
          <span className="shortcut-trigger-label">快捷键</span>
          <kbd>{lastTriggeredShortcut}</kbd>
        </div>
      ) : null}
      <div className="bottom-overlay">
        <BottomToolbar app={app} />
      </div>
      <ShortcutCheatsheet
        isOpen={isShortcutCheatsheetOpen}
        onClose={() => {
          setIsShortcutCheatsheetOpen(false);
        }}
      />
      {editText && (
        <Textarea
          text={editText.text}
          left={editText.left}
          top={editText.top}
          fontSize={editText.fontSize}
          color={editText.color}
          fontFamily={editText.fontFamily}
          onChangeText={text => {
            setEditText(Object.assign({}, editText, { text }));
          }}
          onSubmit={() => {
             // Logic extracted from finishTextEditing effect or similar
             if (editText && editText.text.trim()) {
                if (editText.elementId) {
                  app.update_text_node(editText.elementId, editText.text.trim());
                } else {
                  app.create_text_node(
                    editText.text.trim(),
                    editText.viewLeft,
                    editText.viewTop
                  );
                }
             }
             app.set_editing_id(undefined);
             setEditText(null);
             app.change_cursor_draw_mode(CursorDrawMode.Cursor);
          }}
          onBlur={() => {
             // Same logic as onSubmit for now
             if (editText && editText.text.trim()) {
                if (editText.elementId) {
                  app.update_text_node(editText.elementId, editText.text.trim());
                } else {
                  app.create_text_node(
                    editText.text.trim(),
                    editText.viewLeft,
                    editText.viewTop
                  );
                }
             }
             app.set_editing_id(undefined);
             setEditText(null);
             app.change_cursor_draw_mode(CursorDrawMode.Cursor);
          }}
        />
      )}
    </div>
  );
};

export default Application;
